import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Search, Loader2, Plus, Sparkles, Library, Film, Image, Tag } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import { useHighlightsApi } from './useHighlightsApi'
import TrimRangeSlider from './TrimRangeSlider'

// Tabbed source picker for inserting a segment into a reel.
//
// Sources:
//   - Reel Pool       — key moments + chapters from the source videos that
//                       already contribute a segment to this reel. Default
//                       tab when the pool is non-empty; lets editors grab
//                       moments they skipped without re-running the search.
//   - Content Library — every active vod_catalog item. Tag-filterable.
//   - Mux Stream Manager — every Mux asset, including uploads not yet
//                        registered in vod_catalog. Coming soon.
//   - Image Library    — Cloudflare Images. Coming soon — reels currently
//                        only support video segments; images need an
//                        image-to-video step the worker doesn't yet have.
//
// Each tab has its own search + filter controls. When the user picks an
// item the modal flips to the trim screen (Mux Player + range slider +
// Insert button) — same flow as before. Reel Pool items pre-fill the trim
// window with the moment/chapter's own bounds.

function fmtTime(sec) {
  if (sec == null) return '–'
  const s = Math.floor(sec)
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

// Reuses the same regex as the worker. Mirrors mux-client.js extractMuxPlaybackId.
function extractPid(mediaUrl) {
  if (!mediaUrl) return null
  const m = /stream\.mux\.com\/([A-Za-z0-9]{20,})/.exec(mediaUrl)
  return m ? m[1] : null
}

// A "clip" content item is a Mux asset URL carrying asset_start_time/asset_end_time (created by the
// Mux Stream Editor's "create content item"). Its playback id is the FULL parent asset — the offset
// lives only in these query params. The reel model is asset-ABSOLUTE (segments store absolute
// start_ms/end_ms into the parent; the stitcher + ReelEditor seek by absolute time), so the picker
// must seed the trim window with the clip's absolute bounds. Without this the window defaults to 0 and
// plays the parent from the beginning instead of the clip's real region. Returns ms, or null for a
// full-asset item (no offset → 0-based is already correct).
function extractClipWindowMs(mediaUrl) {
  if (!mediaUrl) return null
  const s = /[?&]asset_start_time=([\d.]+)/.exec(mediaUrl)
  const e = /[?&]asset_end_time=([\d.]+)/.exec(mediaUrl)
  if (!s && !e) return null
  const startMs = s ? Math.round(parseFloat(s[1]) * 1000) : 0
  const endMs = e ? Math.round(parseFloat(e[1]) * 1000) : null
  return { startMs, endMs }
}

// Parse the various tag shapes vod_catalog can return: JSON string, array,
// or null. Always returns a sorted unique string array.
function parseTags(raw) {
  if (!raw) return []
  if (Array.isArray(raw)) return raw.filter(Boolean)
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed.filter(Boolean)
    } catch {}
  }
  return []
}

const TABS = [
  { key: 'reelPool', label: 'Reel Pool',           icon: Sparkles,  active: true,  blurb: "Key moments + chapters from this reel's source videos." },
  { key: 'content',  label: 'Content Library',     icon: Library,   active: true,  blurb: 'Every active video in the content catalog.' },
  { key: 'mux',      label: 'Mux Stream Manager',  icon: Film,      active: false, blurb: 'All Mux assets including uncatalogued uploads. Coming soon — requires content-link lookup before insertion is safe.' },
  { key: 'image',    label: 'Image Library',       icon: Image,     active: false, blurb: 'Cloudflare Images. Coming soon — reels are video-only; inserting an image requires an image→video pipeline (see Step 14h in the plan).' },
]

export default function SourcePickerModal({ position, onClose, onInsert, reelPool, sourceVideos, currentSegments }) {
  const api = useHighlightsApi()
  const { api: rawApi } = useAuth()  // raw fetcher for the non-highlights endpoints (/vod, /tags)
  // Default to Reel Pool if the editor passed a non-empty pool; otherwise
  // fall back to Content Library (e.g. reels assembled entirely from
  // Content Library uploads with no associated key moments / chapters).
  const hasPool = Array.isArray(reelPool) && reelPool.length > 0
  const [tab, setTab] = useState(hasPool ? 'reelPool' : 'content')
  const [picked, setPicked] = useState(null)
  // Trim window for the picked source — defaults to first 20s. Pool picks
  // override this with the moment/chapter's own bounds in pick().
  const [startMs, setStartMs] = useState(0)
  const [endMs, setEndMs] = useState(20000)

  // All available tags (loaded once for tag chip suggestions).
  const [allTags, setAllTags] = useState([])
  useEffect(() => {
    rawApi('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [rawApi])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function pick(item) {
    setPicked(item)
    // Pool picks already know the moment's bounds; pre-fill the trim window
    // so the editor lands inside the moment instead of at t=0.
    if (item._poolMoment) {
      setStartMs(item._poolMoment.start_ms)
      setEndMs(item._poolMoment.end_ms)
    } else {
      // Clip item (media_url carries asset_start_time/asset_end_time) → seed the trim window with the
      // clip's ABSOLUTE bounds so it plays the clip's region, not the parent from t=0. Default to the
      // whole clip. Full-asset item (no offset) → the existing 0-based default.
      const win = extractClipWindowMs(item.media_url)
      if (win) {
        const clipEnd = win.endMs != null ? win.endMs : win.startMs + Math.max(2000, (item.duration || 60) * 1000)
        setStartMs(win.startMs)
        setEndMs(clipEnd)
      } else {
        setStartMs(0)
        setEndMs(Math.min(20000, Math.max(2000, (item.duration || 60) * 1000)))
      }
    }
  }

  function insert() {
    if (!picked) return
    // Pool items carry their source video's mux playback id + title in
    // _poolMoment; Caption/Content rows carry it on the row itself.
    const muxPid = picked._poolMoment?.source_mux_playback_id || extractPid(picked.media_url)
    const sourceTitle = picked._poolMoment?.source_title || picked.title
    // For a query-param clip the reel stores ABSOLUTE asset offsets, so the editor's trim bound must be the
    // clip's absolute end (asset_end_time), not the clip's own length. Matches the reel-read derivation so a
    // just-inserted segment renders/saves correctly before reload. Standalone/full assets → picked.duration.
    const clipWin = picked._poolMoment ? null : extractClipWindowMs(picked.media_url)
    const sourceDurationSeconds = (clipWin && clipWin.endMs != null)
      ? Math.ceil(clipWin.endMs / 1000)
      : picked.duration
    onInsert?.({
      source_tbtv_id: picked.tbtv_id,
      start_ms: Math.round(startMs),
      end_ms: Math.round(endMs),
      _meta: {
        source_title: sourceTitle,
        source_duration_seconds: sourceDurationSeconds,
        source_thumbnail_url: picked.thumbnail_url,
        source_mux_playback_id: muxPid,
      },
    })
    onClose?.()
  }

  const positionLabel =
    position == null ? 'Insert' :
    position === 'start' ? 'Insert at start' :
    position === 'end' ? 'Insert at end' :
    `Insert between segments ${position} and ${position + 1}`

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
    >
      <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 8, width: 'min(820px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{positionLabel}</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {!picked && (
          <>
            {/* Tabs */}
            <div style={{ padding: '0 14px', borderBottom: '1px solid #1f1f1f', display: 'flex', gap: 0 }}>
              {TABS.map(t => {
                const Icon = t.icon
                const active = tab === t.key
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    title={t.blurb}
                    style={{
                      padding: '10px 14px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '2px solid ' + (active ? '#3b82f6' : 'transparent'),
                      color: active ? '#bfdbfe' : (t.active ? '#aaa' : '#666'),
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: active ? 600 : 500,
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      position: 'relative',
                    }}>
                    <Icon size={13} />
                    {t.label}
                    {!t.active && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 8, background: '#332205', color: '#d4a72c', fontWeight: 600 }}>soon</span>}
                  </button>
                )
              })}
            </div>

            {/* Tab body */}
            {tab === 'reelPool' && (
              <ReelPoolPane
                reelPool={reelPool}
                sourceVideos={sourceVideos}
                currentSegments={currentSegments}
                onPick={pick}
              />
            )}

            {tab === 'content' && (
              <PickerListPane
                source="content"
                allTags={allTags}
                onPick={pick}
                loader={async ({ query, tags }) => {
                  const params = new URLSearchParams()
                  params.set('status', 'active')
                  if (query) params.set('search', query)
                  const data = await rawApi(`/vod?${params}`)
                  let items = (Array.isArray(data) ? data : (data.items || []))
                    .filter(i => extractPid(i.media_url))
                  // Tag filter is client-side — vod_catalog stores tags as a
                  // JSON column we already need to parse for display anyway.
                  if (tags && tags.length > 0) {
                    items = items.filter(it => {
                      const itemTags = new Set(parseTags(it.tags))
                      return tags.every(t => itemTags.has(t))
                    })
                  }
                  return { items }
                }}
                emptyMessage="No Mux-backed active videos match these filters."
              />
            )}

            {tab === 'mux' && <ComingSoonPane tab={TABS.find(t => t.key === 'mux')} />}
            {tab === 'image' && <ComingSoonPane tab={TABS.find(t => t.key === 'image')} />}
          </>
        )}

        {picked && (() => {
          const pid = picked._poolMoment?.source_mux_playback_id || extractPid(picked.media_url)
          // Clip items trim within their ABSOLUTE asset window; full-asset items trim 0→duration.
          const clipWin = picked._poolMoment ? null : extractClipWindowMs(picked.media_url)
          const sliderMin = clipWin ? clipWin.startMs : 0
          const sliderMax = clipWin
            ? (clipWin.endMs != null ? clipWin.endMs : clipWin.startMs + Math.max(1000, (picked.duration || 60) * 1000))
            : Math.max(1000, (picked.duration || 60) * 1000)
          return (
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{picked.title || picked.tbtv_id}</div>
              <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace', marginTop: 2 }}>
                {picked.tbtv_id} · {fmtTime(picked.duration)}
              </div>
            </div>
            {pid && (
              <mux-player
                key={`${picked.tbtv_id}-${startMs}-${endMs}`}
                src={`https://stream.mux.com/${pid}.m3u8?asset_start_time=${startMs/1000}&asset_end_time=${endMs/1000}`}
                stream-type="on-demand"
                accent-color="#3b82f6"
                style={{ width: '100%', aspectRatio: '16/9', display: 'block', background: '#000' }}
              />
            )}
            <TrimRangeSlider
              minMs={sliderMin}
              maxMs={sliderMax}
              startMs={startMs}
              endMs={endMs}
              onChange={({ startMs: ns, endMs: ne }) => { setStartMs(ns); setEndMs(ne) }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setPicked(null)}
                style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 13 }}>
                Back
              </button>
              <button onClick={insert}
                style={{ padding: '6px 16px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Plus size={14} /> Insert
              </button>
            </div>
          </div>
          )
        })()}

        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
      </div>
    </div>
  )
}

// Generic searchable/filterable list pane. Loader is responsible for the
// actual data fetch; this component owns the search input, tag chips, and
// list rendering. Items are expected to have at least tbtv_id, title,
// thumbnail_url, duration, media_url; tags are optional.
function PickerListPane({ source, allTags, onPick, loader, emptyMessage }) {
  const [query, setQuery] = useState('')
  const [tags, setTags] = useState(() => new Set())
  const [items, setItems] = useState(null)
  const [err, setErr] = useState(null)
  const loadKey = useRef(0)

  // Debounced loader. Resets the list to null between fetches so the
  // loading state shows on every change.
  useEffect(() => {
    let cancelled = false
    const key = ++loadKey.current
    setItems(null); setErr(null)
    const t = setTimeout(() => {
      loader({ query: query.trim(), tags: Array.from(tags) })
        .then(r => {
          if (cancelled || key !== loadKey.current) return
          setItems(r.items || [])
        })
        .catch(e => { if (!cancelled) setErr(e.message) })
    }, query ? 250 : 0)
    return () => { cancelled = true; clearTimeout(t) }
  }, [source, query, tags])

  // Tags shown as chips — limited to what appears in the current result set
  // so editors don't get a wall of irrelevant league tags. Falls back to the
  // global tags list when the result set is empty (e.g. before first load).
  const tagOptions = useMemo(() => {
    if (!items) return Array.isArray(allTags) ? allTags.slice(0, 40) : []
    const seen = new Set()
    for (const it of items) for (const t of parseTags(it.tags)) seen.add(t)
    return Array.from(seen).sort()
  }, [items, allTags])

  function toggleTag(t) {
    setTags(prev => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t); else next.add(t)
      return next
    })
  }

  return (
    <>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #1f1f1f', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0d1622', border: '1px solid #2a2a2a', borderRadius: 4, padding: '6px 10px' }}>
          <Search size={14} style={{ color: '#888' }} />
          <input
            autoFocus
            type="text"
            placeholder="Search by title…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1, background: 'transparent', border: 'none', color: 'inherit', outline: 'none', fontSize: 13 }}
          />
        </div>
        {tagOptions.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            <Tag size={11} style={{ color: '#666', marginRight: 2 }} />
            {tagOptions.slice(0, 25).map(t => {
              const active = tags.has(t)
              return (
                <button key={t} onClick={() => toggleTag(t)}
                  style={{
                    padding: '2px 8px', borderRadius: 999,
                    background: active ? '#1e3a8a' : 'transparent',
                    border: '1px solid ' + (active ? '#3b82f6' : '#333'),
                    color: active ? '#bfdbfe' : '#888',
                    cursor: 'pointer', fontSize: 11, fontWeight: active ? 600 : 500,
                  }}>
                  {t}
                </button>
              )
            })}
            {tags.size > 0 && (
              <button onClick={() => setTags(new Set())}
                style={{ marginLeft: 4, padding: '2px 6px', background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: 11 }}>
                Clear
              </button>
            )}
          </div>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {err && <div style={{ padding: 16, color: 'crimson', fontSize: 12 }}>{err}</div>}
        {items === null && !err && (
          <div style={{ padding: 24, textAlign: 'center', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        )}
        {items && items.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
            {emptyMessage}
          </div>
        )}
        {items && items.map(it => {
          const itTags = parseTags(it.tags)
          return (
            <div key={it.tbtv_id}
              onClick={() => onPick(it)}
              style={{
                padding: '10px 14px', borderBottom: '1px solid #1a1a1a',
                display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              }}>
              <div style={{ width: 72, height: 40, background: '#000', flexShrink: 0, borderRadius: 3, overflow: 'hidden' }}>
                {it.thumbnail_url && <img src={it.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {it.title || it.tbtv_id}
                </div>
                <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace', marginTop: 2 }}>
                  {it.tbtv_id} · {fmtTime(it.duration)}
                </div>
                {itTags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                    {itTags.slice(0, 6).map(t => (
                      <span key={t} style={{ fontSize: 10, padding: '0 6px', borderRadius: 8, background: '#1f2937', color: '#888' }}>{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

// Reel Pool — key moments + chapters from this reel's source videos.
// Defaults to the first tab when the editor opens the picker. Filters by
// source video, kind (key moment vs chapter), and free-text title match.
function ReelPoolPane({ reelPool, sourceVideos, currentSegments, onPick }) {
  // Pool can be `null` while ReelEditor's per-video fetches are in flight,
  // or `[]` once the load completes with nothing to show.
  const loading = reelPool == null
  const sources = Array.isArray(sourceVideos) ? sourceVideos : []
  const segments = Array.isArray(currentSegments) ? currentSegments : []

  const [sourceFilter, setSourceFilter] = useState(() => new Set(sources.map(s => s.tbtv_id)))
  const [kindFilter, setKindFilter]     = useState(() => new Set(['key_moment', 'chapter', 'search_hit']))
  const [query, setQuery]               = useState('')

  // Keep the source-filter set in sync when ReelEditor re-derives the source
  // list (e.g. on initial fetch finishing). Without this the chips render
  // unselected on first paint and the list shows empty.
  useEffect(() => {
    setSourceFilter(new Set(sources.map(s => s.tbtv_id)))
  }, [sources.map(s => s.tbtv_id).join('|')])

  function toggleSource(id) {
    setSourceFilter(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  function toggleKind(k) {
    setKindFilter(prev => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k); else next.add(k)
      return next
    })
  }

  // "Already in reel" — within 5s of an existing segment's start in the same
  // source video. Cheap; segments list is short.
  function isInReel(row) {
    return segments.some(s =>
      s.source_tbtv_id === row.source_tbtv_id &&
      Math.abs((s.start_ms || 0) - row.start_ms) < 5000
    )
  }

  // Sort: preserve source-video first-appearance order, then start_ms within.
  const sourceOrder = useMemo(() => {
    const m = new Map()
    sources.forEach((s, i) => m.set(s.tbtv_id, i))
    return m
  }, [sources])

  const rows = useMemo(() => {
    if (!Array.isArray(reelPool)) return []
    const q = query.trim().toLowerCase()
    return reelPool
      .filter(r => sourceFilter.has(r.source_tbtv_id))
      .filter(r => kindFilter.has(r.kind))
      .filter(r => !q || (r.title || '').toLowerCase().includes(q))
      .sort((a, b) => {
        const oa = sourceOrder.get(a.source_tbtv_id) ?? 99
        const ob = sourceOrder.get(b.source_tbtv_id) ?? 99
        if (oa !== ob) return oa - ob
        return a.start_ms - b.start_ms
      })
  }, [reelPool, sourceFilter, kindFilter, query, sourceOrder])

  // The picker's pick() handler expects fields it normally pulls from a
  // Caption / Content library row. Synthesize them from the pool item so
  // the trim screen + mux-player + onInsert all keep working unchanged.
  function handlePick(row) {
    onPick({
      tbtv_id: row.source_tbtv_id,
      title: row.title || row.source_title || row.source_tbtv_id,
      duration: row.source_duration_seconds,
      media_url: row.source_mux_playback_id
        ? `https://stream.mux.com/${row.source_mux_playback_id}.m3u8`
        : null,
      thumbnail_url: row.source_thumbnail_url,
      _poolMoment: {
        start_ms: row.start_ms,
        end_ms: row.end_ms,
        source_mux_playback_id: row.source_mux_playback_id,
        source_title: row.source_title,
      },
    })
  }

  const totalKM = (reelPool || []).filter(r => r.kind === 'key_moment').length
  const totalCh = (reelPool || []).filter(r => r.kind === 'chapter').length
  const totalSH = (reelPool || []).filter(r => r.kind === 'search_hit').length

  return (
    <>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #1f1f1f', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 11, color: '#888' }}>
          {loading
            ? <>Loading pool from reel's source videos…</>
            : <>From this reel's source videos — <strong style={{ color: '#bbb' }}>{totalKM}</strong> key moment{totalKM === 1 ? '' : 's'} + <strong style={{ color: '#bbb' }}>{totalCh}</strong> chapter{totalCh === 1 ? '' : 's'}{totalSH > 0 && <> + <strong style={{ color: '#bbb' }}>{totalSH}</strong> search hit{totalSH === 1 ? '' : 's'}</>} across <strong style={{ color: '#bbb' }}>{sources.length}</strong> video{sources.length === 1 ? '' : 's'}.</>
          }
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0d1622', border: '1px solid #2a2a2a', borderRadius: 4, padding: '6px 10px' }}>
          <Search size={14} style={{ color: '#888' }} />
          <input
            autoFocus
            type="text"
            placeholder="Filter pool by title…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1, background: 'transparent', border: 'none', color: 'inherit', outline: 'none', fontSize: 13 }}
          />
        </div>

        {sources.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4 }}>Source</span>
            {sources.map(s => {
              const active = sourceFilter.has(s.tbtv_id)
              return (
                <button key={s.tbtv_id} onClick={() => toggleSource(s.tbtv_id)}
                  title={s.tbtv_id}
                  style={{
                    padding: '2px 8px', borderRadius: 999,
                    background: active ? '#1e3a8a' : 'transparent',
                    border: '1px solid ' + (active ? '#3b82f6' : '#333'),
                    color: active ? '#bfdbfe' : '#888',
                    cursor: 'pointer', fontSize: 11, fontWeight: active ? 600 : 500,
                    maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                  {s.title || s.tbtv_id}
                </button>
              )
            })}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4 }}>Type</span>
          {[
            { k: 'key_moment', label: '💫 Key moments' },
            { k: 'chapter',    label: '📑 Chapters' },
            { k: 'search_hit', label: '🔍 Search hits' },
          ].map(({ k, label }) => {
            const active = kindFilter.has(k)
            return (
              <button key={k} onClick={() => toggleKind(k)}
                style={{
                  padding: '2px 8px', borderRadius: 999,
                  background: active ? '#1e3a8a' : 'transparent',
                  border: '1px solid ' + (active ? '#3b82f6' : '#333'),
                  color: active ? '#bfdbfe' : '#888',
                  cursor: 'pointer', fontSize: 11, fontWeight: active ? 600 : 500,
                }}>
                {label}
              </button>
            )
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && (
          <div style={{ padding: 24, textAlign: 'center', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
            {totalKM + totalCh === 0
              ? "No key moments or chapters found for this reel's source videos. Try Content Library to add a video from scratch."
              : 'No rows match the current filters.'}
          </div>
        )}
        {!loading && rows.map(row => {
          const inReel = isInReel(row)
          const isKM = row.kind === 'key_moment'
          const isCh = row.kind === 'chapter'
          const isSH = row.kind === 'search_hit'
          const icon = isSH ? '🔍' : (isKM ? '💫' : '📑')
          const fallbackTitle = isSH
            ? '(search hit)'
            : (isKM ? '(untitled moment)' : '(untitled chapter)')
          // For search hits the row's `title` is the query; the cue text is
          // in `excerpt`. The excerpt may contain <mark> tags from the FTS
          // snippet builder — we render those as bold spans.
          return (
            <div key={row.row_id}
              onClick={() => handlePick(row)}
              style={{
                padding: '10px 14px', borderBottom: '1px solid #1a1a1a',
                display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
              }}>
              <div style={{ width: 36, flexShrink: 0, fontSize: 18, textAlign: 'center', paddingTop: 1 }}>
                {icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                    {isSH
                      ? <>Search: <span style={{ color: '#bfdbfe' }}>"{row.title}"</span></>
                      : (row.title || fallbackTitle)}
                  </div>
                  {isKM && row.quality_score != null && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#bbb', fontWeight: 600 }}>
                      {row.quality_score.toFixed(2)}
                    </span>
                  )}
                  {isKM && Array.isArray(row.matched_queries) && row.matched_queries.length > 0 && (
                    <span
                      title={`This AI moment's text matches the original search ${row.matched_queries.map(q => `"${q}"`).join(', ')}`}
                      style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1e3a8a', color: '#bfdbfe', fontWeight: 600 }}
                    >
                      🔍 "{row.matched_queries[0]}"{row.matched_queries.length > 1 ? ` +${row.matched_queries.length - 1}` : ''}
                    </span>
                  )}
                  {inReel && (
                    <span title="A segment in this reel already starts within 5s of this row"
                      style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1e3a26', color: '#86efac', fontWeight: 600 }}>
                      in reel
                    </span>
                  )}
                </div>
                {isSH && row.excerpt && (
                  <div
                    style={{ fontSize: 12, color: '#ccc', marginTop: 3, lineHeight: 1.4 }}
                    dangerouslySetInnerHTML={{ __html: row.excerpt }}
                  />
                )}
                <div style={{ fontSize: 11, color: '#888', marginTop: isSH ? 4 : 2, fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>
                    {row.source_title || row.source_tbtv_id}
                    {' · '}
                    {fmtTime((isSH ? (row.anchor_ms ?? row.start_ms) : row.start_ms) / 1000)}
                  </span>
                  {row.moment_type && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#bbb', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {row.moment_type.replace('_', ' ')}
                    </span>
                  )}
                  {isSH && row.match_source && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#bbb', fontFamily: 'inherit', fontWeight: 600 }}>
                      {row.match_source}
                    </span>
                  )}
                  {isSH && row.in_key_moment && (
                    <span title="This cue falls inside a key moment window"
                      style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#312e81', color: '#c4b5fd', fontFamily: 'inherit', fontWeight: 600 }}>
                      in KM
                    </span>
                  )}
                  {!isSH && row.source === 'tbtv_ai' && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1e1b4b', color: '#c4b5fd', fontFamily: 'inherit', fontWeight: 600 }}>
                      ai{row.detector ? `·${row.detector}` : ''}
                    </span>
                  )}
                  {!isSH && row.source === 'mux' && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#888', fontFamily: 'inherit', fontWeight: 600 }}>
                      mux
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

function ComingSoonPane({ tab }) {
  const Icon = tab.icon
  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: '#888', textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: 28, background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={24} style={{ color: '#666' }} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#bbb' }}>{tab.label}</div>
      <div style={{ fontSize: 12, maxWidth: 460, lineHeight: 1.5 }}>{tab.blurb}</div>
    </div>
  )
}
